<script setup lang="ts">
import type { Component } from "vue";
import { computed } from "vue";

import DropdownItemWrapper from "~/components/dumb/DropdownItemWrapper.vue";
import ShortcutStrokes from "~/components/dumb/ShortcutStrokes.vue";
import { getStrokesForPlatform } from "~/constants/command";
import { CheckIcon, ChevronRightIcon } from "~/icons";
import type { CommandId } from "~/shared/enums";
import { useAppStore, usePageStore } from "~/stores";

const props = defineProps<{
  title: string;
  titleClass?: string;
  icon?: Component;
  iconArgs?: object;
  iconAfter?: Component;
  iconAfterArgs?: object;
  selected?: boolean;
  disabled?: boolean;
  subtitle?: string;
  strikethrough?: boolean;
  commandId?: CommandId;
  rightText?: string | number;
  rightTextStyle?: string;
  isSubmenu?: boolean;
  dataTestid?: string;
  showIconAfterOnHover?: boolean;
  showDot?: boolean;
  isChip?: boolean;
}>();

const emit = defineEmits<{
  click: [event: Event];
}>();

const appStore = useAppStore();
const pageStore = usePageStore();

const strokes = computed(() =>
  props.commandId ? getStrokesForPlatform(appStore.commandsList, props.commandId, pageStore.isMac) : undefined
);

const onClick = (event: Event) => {
  if (!pageStore.isMobile && props.isSubmenu) {
    event.stopPropagation();
  }
  emit("click", event);
};
</script>

<template>
  <DropdownItemWrapper
    :selected="selected"
    :no-padding="!pageStore.isMobile && isChip && selected"
    :data-testid="dataTestid"
    class="max-w-full transform-gpu"
    :class="disabled && 'opacity-50'"
    @click="onClick">
    <component
      :is="icon"
      v-if="icon"
      class="mr-2 shrink-0"
      :class="[
        !iconArgs && `${pageStore.isMobile ? 'icon-md' : 'icon-sm'} ${selected ? 'text-primary-base' : 'text-lt'}`,
      ]"
      v-bind="iconArgs" />

    <div class="mr-2 flex flex-col overflow-hidden">
      <div
        :title="title"
        class="truncate text-md select-none"
        :class="[
          titleClass,
          {
            'line-through': strikethrough,
            'text-base': pageStore.isMobile,
            'text-sm': !pageStore.isMobile,
          },
        ]">
        {{ title }}
      </div>
      <div
        v-if="subtitle"
        :title="subtitle"
        class="flex-1 truncate text-lt select-none"
        :class="pageStore.isMobile ? 'text-sm' : 'text-xs'">
        {{ subtitle }}
      </div>
    </div>

    <template #actions>
      <div class="flex flex-1 items-center justify-end gap-2">
        <ShortcutStrokes :strokes="strokes" :disabled="Boolean(disabled)" />
        <div v-if="showDot" class="flex icon-md items-center justify-center rounded-full focus-ring-none">
          <div class="size-2 rounded-full bg-primary-base" />
        </div>
        <component
          :is="iconAfter"
          v-if="iconAfter"
          v-bind="iconAfterArgs"
          :class="showIconAfterOnHover ? 'hidden [[data-focused]_&]:block' : ''" />
        <div
          v-if="rightText"
          class="flex items-center justify-center"
          :class="[
            rightTextStyle
              ? rightTextStyle
              : 'min-h-4 min-w-4 rounded bg-primary-base px-[3px] text-[10px]/[13px] font-semibold text-oncolor',
          ]">
          {{ rightText }}
        </div>
        <ChevronRightIcon v-if="isSubmenu" class="text-vlt" :class="pageStore.isMobile ? 'icon-md' : 'icon-sm'" />
        <CheckIcon v-if="pageStore.isMobile && selected" class="icon-md text-primary-base" />
      </div>
    </template>
  </DropdownItemWrapper>
</template>
